import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import Swal from 'sweetalert2'

import axios from '../api/axios'
import Loading from '../components/Loading'

const RegisterPage = () => {
	const [isRegisterSuccess, setIsRegisterSuccess] = useState(false)
	const [loading, setLoading] = useState(false)
	const [workstream, setWorkstream] = useState()
	const [company, setCompany] = useState()

	const navigate = useNavigate()
	const { state } = useLocation()

	const {
		handleSubmit,
		register,
		setValue,
		formState: { errors }
	} = useForm({
		defaultValues: state
			? {
					employeeName: state.name,
					email: state.username,
					email2: state.email,
					accept: true
			  }
			: {}
	})

	useEffect(() => {
		axios.get('/POP_Register/RegisterBindControl').then(({ data }) => {
			setWorkstream(data.tb_workstream)
			setCompany(data.tb_company)
		})
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (state) {
			setValue('comIdFk', state.comID)
		}
	}, [state, setValue])

	const onSubmit = async data => {
		const transformData = {
			...data,
			listItems: data.listItems.map(item => ({
				empIdFk: 0,
				workStreamIdFk: parseInt(item)
			}))
		}

		setLoading(true)

		try {
			await axios.post('/POP_Register/RegisterSave', transformData)

			setIsRegisterSuccess(true)

			Swal.fire({
				title: 'Success',
				icon: 'success',
				confirmButtonText: 'Ok'
			})
		} catch (err) {
			Swal.fire({
				title: 'Error!',
				text: err.response?.data?.message ?? err.response?.data?.title,
				icon: 'error',
				confirmButtonText: 'Ok'
			})
		} finally {
			setLoading(false)
		}
	}

	if (!company) return <Loading />

	return (
		<>
			{loading && <Loading />}

			<div className="register-page">
				<div className="register-logo">
					<img
						alt=""
						src={`${process.env.PUBLIC_URL}/images/logos/logo-one-platform.png`}
					/>
				</div>

				<>
					{isRegisterSuccess ? (
						<>
							<h1 className="text-2xl mt-5 uppercase">
								<span className="text-ci1">Register</span>{' '}
								<span className="text-ci2">Successfully</span>
							</h1>
							<p className="text-lg mt-5 text-center w-96">
								You will receive a confirmation email as soon as the admin system
								approved your information
							</p>
							<div className="flex mt-5 w-96">
								<button
									className="flex-1 justify-center btn border border-ci1 text-ci1 hover:border-ci2 hover:text-ci2"
									type="button"
									onClick={() => (window.location.href = '/login')}
								>
									OK
								</button>
							</div>
						</>
					) : (
						<>
							<h1 className="text-2xl mt-5 uppercase">
								<span className="text-ci1">Register</span>{' '}
								<span className="text-ci2">Form</span>
							</h1>

							<form
								className="register-container"
								onSubmit={handleSubmit(onSubmit)}
							>
								<div className="mt-4">
									<label>
										Full Name <sup className="text-red-500">*</sup>
									</label>
									<input
										type="text"
										placeholder="Enter full name"
										className={errors.employeeName ? 'error' : ''}
										{...register('employeeName', {
											required: 'Full name is required'
										})}
									/>
									{errors.employeeName && (
										<div className="error-message">
											{errors.employeeName.message}
										</div>
									)}
								</div>

								<div className="mt-4">
									<label>
										Work Stream <sup className="text-red-500">*</sup>
									</label>
									<div className="grid grid-cols-2 gap-3 mt-2 md:grid-cols-3">
										{workstream?.map(item => (
											<label
												key={item.workstreamID}
												className="flex items-center"
											>
												<input
													type="checkbox"
													value={item.workstreamID}
													{...register('listItems', {
														required: true,
														valueAsNumber: true
													})}
												/>
												<span className="ml-2">{item.workstreamName}</span>
											</label>
										))}
									</div>
									{errors.listItems && (
										<div className="error-message">
											Please select at least 1 workstream
										</div>
									)}
								</div>

								<div className="mt-4">
									<label>
										Company <sup className="text-red-500">*</sup>
									</label>
									<select
										className={errors.comIdFk ? 'error' : ''}
										{...register('comIdFk', {
											required: 'Company is required'
										})}
									>
										<option value="">Select Company</option>
										{company?.map(item => (
											<option key={item.comID} value={item.comID}>
												{item.comCode}
											</option>
										))}
									</select>
									{errors.comIdFk && (
										<div className="error-message">
											{errors.comIdFk.message}
										</div>
									)}
								</div>

								<div className="mt-3">
									<label>
										Email Address <sup className="text-red-500">*</sup>
									</label>
									<input
										type="email"
										placeholder="Enter email address"
										className={errors.email ? 'error' : ''}
										{...register('email', { required: 'Email is required' })}
									/>
									{errors.email && (
										<div className="error-message">{errors.email.message}</div>
									)}
								</div>

								<div className="mt-3">
									<label className="flex items-start">
										<input
											type="checkbox"
											{...register('accept', {
												required: 'You must accept terms and conditions'
											})}
										/>
										<span className="-mt-[1px] ml-2">
											Accept{' '}
											<a
												href="https://pttpdpa.pttplc.com/Privacy/213214"
												target="_blank"
												rel="noreferrer"
											>
												privacy notice for the operators in PTT Group
											</a>{' '}
											<sup className="text-red-500">*</sup>
										</span>
									</label>
									{errors.accept && (
										<div className="error-message">{errors.accept.message}</div>
									)}
								</div>

								<div className="flex flex-1 mt-5">
									<button
										className="flex-1 justify-center btn bg-ci2 text-white hover:bg-ci4"
										type="submit"
									>
										Register
									</button>
								</div>

								{!state && (
									<div className="flex flex-1 mt-3">
										<button
											className="flex-1 justify-center btn border border-ci1 text-ci1 hover:border-ci2 hover:text-ci2"
											type="button"
											onClick={() => navigate('/login', { replace: true })}
										>
											Already have an account? Sign in
										</button>
									</div>
								)}
							</form>
						</>
					)}
				</>
			</div>
		</>
	)
}

export default RegisterPage
